import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Plus, X, Check, Briefcase, Wrench, Users, Sparkles } from 'lucide-react';
import { AppSettings, Tool, DEFAULT_CRITICALITY_LEVELS } from '../types';
import { updateCompanyIndustry } from '../services/storageService';
import { extractEmailDomain, getCurrentUserEmailDomain, inviteEmployee } from '../services/authService';
import { CsvImporter } from '../components/CsvImporter';

// ============================================================
// Constants
// ============================================================

const CURRENCIES = [
  { code: 'EUR', symbol: '€', label: 'Euro (EUR)' },
  { code: 'USD', symbol: '$', label: 'US Dollar (USD)' },
  { code: 'GBP', symbol: '£', label: 'British Pound (GBP)' },
  { code: 'CHF', symbol: 'CHF', label: 'Swiss Franc (CHF)' },
  { code: 'CAD', symbol: 'CA$', label: 'Canadian Dollar (CAD)' },
  { code: 'SEK', symbol: 'kr', label: 'Swedish Krona (SEK)' },
];

const ALL_CATEGORIES = [
  'Communication & Messaging',
  'Project Management',
  'Development & Engineering',
  'Design & Creative',
  'Cloud & Infrastructure',
  'Security & Compliance',
  'HR & People',
  'Finance & Accounting',
  'Marketing & Sales',
  'Analytics & BI',
  'Customer Support',
  'IT Operations',
];

const INDUSTRIES: { value: string; label: string }[] = [
  { value: 'tech', label: 'Tech / Software' },
  { value: 'finance', label: 'Finance / Banking' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail / E-commerce' },
  { value: 'education', label: 'Education' },
  { value: 'consulting', label: 'Consulting / Services' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'marketing', label: 'Marketing / Media' },
  { value: 'other', label: 'Other' },
];

const INDUSTRY_CATEGORIES: Record<string, string[]> = {
  tech: ['Development & Engineering', 'Cloud & Infrastructure', 'Communication & Messaging', 'Project Management', 'Design & Creative', 'Security & Compliance'],
  finance: ['Finance & Accounting', 'Security & Compliance', 'Analytics & BI', 'Communication & Messaging', 'HR & People'],
  healthcare: ['Security & Compliance', 'HR & People', 'Communication & Messaging', 'Analytics & BI'],
  retail: ['Marketing & Sales', 'Analytics & BI', 'Customer Support', 'Finance & Accounting'],
  education: ['Communication & Messaging', 'Project Management', 'HR & People', 'Design & Creative'],
  consulting: ['Project Management', 'Communication & Messaging', 'Analytics & BI', 'Finance & Accounting'],
  manufacturing: ['IT Operations', 'Project Management', 'Finance & Accounting', 'Communication & Messaging'],
  marketing: ['Design & Creative', 'Marketing & Sales', 'Analytics & BI', 'Communication & Messaging', 'Project Management'],
  other: ['Communication & Messaging', 'Project Management', 'Finance & Accounting'],
};

const STEP_TITLES = ['Set up your workspace', 'Add your first tools', 'Invite your team'];
const STEP_ICONS = [Briefcase, Wrench, Users];
const DEFAULT_OASIX_CATEGORY = 'Saas Manager';
const DEFAULT_OASIX_TAGS = ['Governance', 'Training'];

// ============================================================
// Interfaces
// ============================================================

interface OnboardingViewProps {
  settings: AppSettings;
  onUpdateSettings: (s: AppSettings) => Promise<void>;
  companyId: string | null;
  onAddTool: (tool: Tool) => Promise<void>;
  onReloadSettings: () => Promise<AppSettings>;
}

interface InlineToolRow {
  name: string;
  category: string;
  basePrice: string;
  renewalDate: string;
}

interface InviteRow {
  email: string;
  role: 'admin' | 'user';
  team: string;
}

// ============================================================
// Component
// ============================================================

export const OnboardingView: React.FC<OnboardingViewProps> = ({
  settings,
  onUpdateSettings,
  companyId,
  onAddTool,
  onReloadSettings,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Step 1 state
  const [currency, setCurrency] = useState(settings.currency || 'EUR');
  const [industry, setIndustry] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    settings.categories?.length ? settings.categories : [DEFAULT_OASIX_CATEGORY]
  );
  const [customCategory, setCustomCategory] = useState('');

  // Step 2 state
  const [toolRows, setToolRows] = useState<InlineToolRow[]>([
    { name: '', category: '', basePrice: '', renewalDate: '' },
    { name: '', category: '', basePrice: '', renewalDate: '' },
    { name: '', category: '', basePrice: '', renewalDate: '' },
    { name: '', category: '', basePrice: '', renewalDate: '' },
  ]);
  const [toolsAdded, setToolsAdded] = useState(0);

  // Step 3 state
  const [inviteRows, setInviteRows] = useState<InviteRow[]>([
    { email: '', role: 'user', team: '' },
    { email: '', role: 'user', team: '' },
  ]);
  const [invitesSent, setInvitesSent] = useState(0);
  const [inviteErrors, setInviteErrors] = useState<string[]>([]);

  // ============================================================
  // Step 1 handlers
  // ============================================================

  const handleIndustryChange = (value: string) => {
    setIndustry(value);
    const suggested = INDUSTRY_CATEGORIES[value] || [];
    setSelectedCategories(suggested);
  };

  const toggleCategory = (cat: string) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const addCustomCategory = () => {
    const trimmed = customCategory.trim();
    if (trimmed && !selectedCategories.includes(trimmed) && !ALL_CATEGORIES.includes(trimmed)) {
      setSelectedCategories(prev => [...prev, trimmed]);
      setCustomCategory('');
    }
  };

  const saveStep1 = async () => {
    if (selectedCategories.length === 0) {
      setError('Please select at least one category.');
      return false;
    }
    setError('');
    setSaving(true);
    try {
      const currencyObj = CURRENCIES.find(c => c.code === currency);
      const mergedCategories = [...new Set([...selectedCategories, DEFAULT_OASIX_CATEGORY])];
      const mergedTags = [...new Set([...(settings.tags || []), ...DEFAULT_OASIX_TAGS])];
      await onUpdateSettings({
        ...settings,
        currency,
        currencySymbol: currencyObj?.symbol || currency,
        categories: mergedCategories,
        tags: mergedTags,
      });
      if (companyId && industry) {
        await updateCompanyIndustry(companyId, industry);
      }
      return true;
    } catch (err: any) {
      setError(err?.message || 'Failed to save settings.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // Step 2 handlers
  // ============================================================

  const updateToolRow = (index: number, field: keyof InlineToolRow, value: string) => {
    setToolRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addToolRow = () => {
    setToolRows(prev => [...prev, { name: '', category: '', basePrice: '', renewalDate: '' }]);
  };

  const removeToolRow = (index: number) => {
    if (toolRows.length <= 1) return;
    setToolRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleCsvImport = useCallback((data: string[][]) => {
    if (data.length <= 1) return;
    const headers = data[0].map(h => h.toLowerCase().trim());
    const nameIdx = headers.findIndex(h => h.includes('name') || h.includes('tool'));
    const catIdx = headers.findIndex(h => h.includes('category') || h.includes('cat'));
    const priceIdx = headers.findIndex(h => h.includes('price') || h.includes('cost'));
    const dateIdx = headers.findIndex(h => h.includes('renewal') || h.includes('date'));

    const imported: InlineToolRow[] = data.slice(1).filter(row => row.some(cell => cell.trim())).map(row => ({
      name: nameIdx >= 0 ? row[nameIdx]?.trim() || '' : row[0]?.trim() || '',
      category: catIdx >= 0 ? row[catIdx]?.trim() || '' : '',
      basePrice: priceIdx >= 0 ? row[priceIdx]?.trim() || '' : '',
      renewalDate: dateIdx >= 0 ? row[dateIdx]?.trim() || '' : '',
    }));

    if (imported.length > 0) {
      setToolRows(prev => {
        const nonEmpty = prev.filter(r => r.name.trim());
        return [...nonEmpty, ...imported];
      });
    }
  }, []);

  const saveStep2 = async () => {
    setSaving(true);
    setError('');
    let count = 0;
    try {
      const validRows = toolRows.filter(r => r.name.trim());
      for (const row of validRows) {
        const tool: Tool = {
          id: crypto.randomUUID(),
          name: row.name.trim(),
          category: row.category || selectedCategories[0] || '',
          importance: DEFAULT_CRITICALITY_LEVELS.find(l => l.sortOrder === 1)?.id ?? DEFAULT_CRITICALITY_LEVELS[0]?.id ?? 'Supporting',
          pricingModel: 'Fixed',
          basePrice: parseFloat(row.basePrice) || 0,
          userCount: 1,
          monthlyCost: parseFloat(row.basePrice) || 0,
          complexity: 1,
          teamLiteracy: 5,
          renewalDate: row.renewalDate || '',
          noticeDeadline: '',
          satisfactionScore: 0,
          assignedUserIds: [],
          tags: [],
          feedback: [],
        };
        await onAddTool(tool);
        count++;
      }
      setToolsAdded(count);
      return true;
    } catch (err: any) {
      setError(err?.message || 'Failed to add tools.');
      setToolsAdded(count);
      return count > 0;
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // Step 3 handlers
  // ============================================================

  const updateInviteRow = (index: number, field: keyof InviteRow, value: string) => {
    setInviteRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addInviteRow = () => {
    setInviteRows(prev => [...prev, { email: '', role: 'user', team: '' }]);
  };

  const removeInviteRow = (index: number) => {
    if (inviteRows.length <= 1) return;
    setInviteRows(prev => prev.filter((_, i) => i !== index));
  };

  const saveStep3 = async () => {
    setSaving(true);
    setError('');
    setInviteErrors([]);
    let count = 0;
    const errors: string[] = [];
    try {
      const validRows = inviteRows.filter(r => r.email.trim());
      const newTeams = new Set<string>();
      const currentUserDomain = await getCurrentUserEmailDomain();
      const configuredDomains = (settings.allowedEmailDomains || []).map((d) => d.toLowerCase());
      const baselineDomain = configuredDomains.length > 0 ? null : currentUserDomain;
      const externalRows = validRows.filter((row) => {
        const rowDomain = extractEmailDomain(row.email.trim());
        if (!rowDomain) return false;
        if (configuredDomains.length > 0) return !configuredDomains.includes(rowDomain.toLowerCase());
        return !!baselineDomain && rowDomain !== baselineDomain;
      });

      if (externalRows.length > 0) {
        const listed = externalRows.map((row) => row.email.trim()).join('\n- ');
        const orgDomainsLabel = configuredDomains.length > 0
          ? configuredDomains.join(', ')
          : (baselineDomain || 'not configured');
        const shouldContinue = window.confirm(
          `External email domains detected:\n- ${listed}\n\nInternal domain(s): ${orgDomainsLabel}\n\nDo you want to continue sending these invitations?`
        );
        if (!shouldContinue) {
          setInviteErrors(['Invitation batch cancelled (external domains not confirmed).']);
          return false;
        }
      }

      for (const row of validRows) {
        try {
          const inferredName = row.email.trim().split('@')[0].replace(/[._-]+/g, ' ').trim() || row.email.trim();
          await inviteEmployee(row.email.trim(), {
            name: inferredName,
            role: row.role === 'admin' ? 'admin' : 'user',
            team: row.team.trim() || undefined,
          });
          if (row.team.trim()) newTeams.add(row.team.trim());
          count++;
        } catch (err: any) {
          errors.push(`${row.email}: ${err?.message || 'Failed'}`);
        }
      }

      if (newTeams.size > 0) {
        const mergedTeams = [...new Set([...(settings.teams || []), ...newTeams])];
        await onUpdateSettings({ ...settings, teams: mergedTeams });
      }

      setInvitesSent(count);
      setInviteErrors(errors);
      return true;
    } catch (err: any) {
      setError(err?.message || 'Failed to invite team members.');
      return count > 0;
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // Completion handler
  // ============================================================

  const completeOnboarding = async () => {
    setSaving(true);
    setError('');
    try {
      const mergedCategories = [...new Set([...selectedCategories, DEFAULT_OASIX_CATEGORY])];
      const mergedTags = [...new Set([...(settings.tags || []), ...DEFAULT_OASIX_TAGS])];
      await onUpdateSettings({
        ...settings,
        categories: mergedCategories,
        tags: mergedTags,
        onboardingCompleted: true,
      });
      await onReloadSettings();
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to complete onboarding. Please retry.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // Navigation
  // ============================================================

  const handleNext = async () => {
    if (step === 0) {
      const ok = await saveStep1();
      if (ok) setStep(1);
    } else if (step === 1) {
      const hasTools = toolRows.some(r => r.name.trim());
      if (hasTools) {
        const ok = await saveStep2();
        if (ok) setStep(2);
      } else {
        setStep(2);
      }
    } else if (step === 2) {
      const hasInvites = inviteRows.some(r => r.email.trim());
      if (hasInvites) {
        const ok = await saveStep3();
        if (ok) setStep(3);
      } else {
        setStep(3);
      }
    }
  };

  const handleSkip = () => {
    if (step === 1) setStep(2);
    else if (step === 2) setStep(3);
  };

  const handleBack = () => {
    if (step > 0 && step < 3) setStep(step - 1);
  };

  // ============================================================
  // Render helpers
  // ============================================================

  const allTeamsEntered = inviteRows.map(r => r.team.trim()).filter(Boolean);
  const uniqueTeams = [...new Set([...(settings.teams || []), ...allTeamsEntered])];

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEP_TITLES.map((title, i) => {
        const Icon = STEP_ICONS[i];
        const isActive = i === step;
        const isDone = i < step || step === 3;
        return (
          <React.Fragment key={i}>
            {i > 0 && <div className={`h-0.5 w-8 sm:w-12 ${isDone ? 'bg-indigo-500' : 'bg-slate-200'}`} />}
            <div className="flex flex-col items-center gap-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                isDone ? 'bg-indigo-600 text-white' : isActive ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-500' : 'bg-slate-100 text-slate-400'
              }`}>
                {isDone && !isActive ? <Check size={18} /> : <Icon size={18} />}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${isActive ? 'text-indigo-600' : 'text-slate-400'}`}>{title}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  // ============================================================
  // Step 1: Configuration
  // ============================================================

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800 mb-1">Set up your workspace</h2>
        <p className="text-sm text-slate-500">Configure your currency and tool categories to get started.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Currency</label>
        <select
          value={currency}
          onChange={e => setCurrency(e.target.value)}
          className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white"
        >
          {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Industry</label>
        <select
          value={industry}
          onChange={e => handleIndustryChange(e.target.value)}
          className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white"
        >
          <option value="">Select your industry...</option>
          {INDUSTRIES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
        </select>
        {industry && <p className="text-xs text-slate-400 mt-1">Categories have been suggested based on your industry. Feel free to adjust.</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Tool categories</label>
        <div className="flex flex-wrap gap-2">
          {ALL_CATEGORIES.map(cat => {
            const isSelected = selectedCategories.includes(cat);
            return (
              <button
                key={cat}
                onClick={() => toggleCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  isSelected
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'
                }`}
              >
                {cat}
              </button>
            );
          })}
          {selectedCategories.filter(c => !ALL_CATEGORIES.includes(c)).map(cat => (
            <button
              key={cat}
              onClick={() => toggleCategory(cat)}
              className="px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-600 text-white border border-indigo-600 flex items-center gap-1"
            >
              {cat} <X size={12} />
            </button>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <input
            type="text"
            value={customCategory}
            onChange={e => setCustomCategory(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomCategory())}
            placeholder="Add custom category..."
            className="flex-1 p-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
          />
          <button
            onClick={addCustomCategory}
            disabled={!customCategory.trim()}
            className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200 disabled:opacity-40"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // Step 2: Add Tools
  // ============================================================

  const renderStep2 = () => (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-800 mb-1">Add your first tools</h2>
        <p className="text-sm text-slate-500">Enter the software tools your company uses. You can also import from CSV.</p>
      </div>

      <div className="space-y-3">
        {toolRows.map((row, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-start">
            <div className="col-span-4">
              {i === 0 && <label className="block text-xs font-medium text-slate-500 mb-1">Name</label>}
              <input
                type="text"
                value={row.name}
                onChange={e => updateToolRow(i, 'name', e.target.value)}
                placeholder="e.g. Slack"
                className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
            <div className="col-span-3">
              {i === 0 && <label className="block text-xs font-medium text-slate-500 mb-1">Category</label>}
              <select
                value={row.category}
                onChange={e => updateToolRow(i, 'category', e.target.value)}
                className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
              >
                <option value="">Select...</option>
                {selectedCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              {i === 0 && <label className="block text-xs font-medium text-slate-500 mb-1">Price/mo</label>}
              <input
                type="number"
                value={row.basePrice}
                onChange={e => updateToolRow(i, 'basePrice', e.target.value)}
                placeholder="0"
                min="0"
                className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
            <div className="col-span-2">
              {i === 0 && <label className="block text-xs font-medium text-slate-500 mb-1">Renewal</label>}
              <input
                type="date"
                value={row.renewalDate}
                onChange={e => updateToolRow(i, 'renewalDate', e.target.value)}
                className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
            <div className="col-span-1 flex items-end">
              {i === 0 && <label className="block text-xs font-medium text-transparent mb-1">&nbsp;</label>}
              <button
                onClick={() => removeToolRow(i)}
                className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                title="Remove"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={addToolRow}
          className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
        >
          <Plus size={16} /> Add another
        </button>
        <div className="h-4 w-px bg-slate-200" />
        <CsvImporter onImport={handleCsvImport} label="Import CSV" />
      </div>
    </div>
  );

  // ============================================================
  // Step 3: Invite Team
  // ============================================================

  const renderStep3 = () => (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-800 mb-1">Invite your team</h2>
        <p className="text-sm text-slate-500">Add team members who will use Oasix. They'll receive an email invitation.</p>
      </div>

      <div className="space-y-3">
        {inviteRows.map((row, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-start">
            <div className="col-span-5">
              {i === 0 && <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>}
              <input
                type="email"
                value={row.email}
                onChange={e => updateInviteRow(i, 'email', e.target.value)}
                placeholder="colleague@company.com"
                className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
            <div className="col-span-2">
              {i === 0 && <label className="block text-xs font-medium text-slate-500 mb-1">Role</label>}
              <select
                value={row.role}
                onChange={e => updateInviteRow(i, 'role', e.target.value)}
                className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="col-span-4">
              {i === 0 && <label className="block text-xs font-medium text-slate-500 mb-1">Team</label>}
              <input
                type="text"
                value={row.team}
                onChange={e => updateInviteRow(i, 'team', e.target.value)}
                list={`teams-list-${i}`}
                placeholder="e.g. Engineering"
                className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              />
              <datalist id={`teams-list-${i}`}>
                {uniqueTeams.map(t => <option key={t} value={t} />)}
              </datalist>
            </div>
            <div className="col-span-1 flex items-end">
              {i === 0 && <label className="block text-xs font-medium text-transparent mb-1">&nbsp;</label>}
              <button
                onClick={() => removeInviteRow(i)}
                className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                title="Remove"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={addInviteRow}
        className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
      >
        <Plus size={16} /> Add another
      </button>

      {inviteErrors.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm font-medium text-amber-700 mb-1">Some invitations failed:</p>
          {inviteErrors.map((err, i) => <p key={i} className="text-xs text-amber-600">{err}</p>)}
        </div>
      )}
    </div>
  );

  // ============================================================
  // Completion screen
  // ============================================================

  const renderCompletion = () => (
    <div className="text-center space-y-6 py-4">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <Sparkles className="text-green-600" size={28} />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">You're all set!</h2>
        <p className="text-slate-500">Your workspace is ready to go.</p>
      </div>

      <div className="bg-slate-50 rounded-xl p-6 text-left space-y-3 max-w-sm mx-auto">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">Categories configured</span>
          <span className="font-semibold text-slate-800">{selectedCategories.length}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">Tools added</span>
          <span className="font-semibold text-slate-800">{toolsAdded}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">Team members invited</span>
          <span className="font-semibold text-slate-800">{invitesSent}</span>
        </div>
      </div>

      <button
        onClick={completeOnboarding}
        disabled={saving}
        className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50"
      >
        {saving ? 'Loading...' : 'Go to Dashboard'}
      </button>
    </div>
  );

  // ============================================================
  // Main render
  // ============================================================

  const isCompletionStep = step === 3;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 sm:p-8 overflow-y-auto flex-1">
          {!isCompletionStep && renderStepIndicator()}

          {step === 0 && renderStep1()}
          {step === 1 && renderStep2()}
          {step === 2 && renderStep3()}
          {step === 3 && renderCompletion()}

          {error && (
            <div className="mt-4 bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">
              {error}
            </div>
          )}
        </div>

        {!isCompletionStep && (
          <div className="border-t border-slate-200 px-6 sm:px-8 py-4 flex items-center justify-between bg-slate-50">
            <div>
              {step > 0 && (
                <button
                  onClick={handleBack}
                  disabled={saving}
                  className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-800 font-medium disabled:opacity-40"
                >
                  <ChevronLeft size={16} /> Back
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {step > 0 && (
                <button
                  onClick={handleSkip}
                  disabled={saving}
                  className="text-sm text-slate-400 hover:text-slate-600 font-medium disabled:opacity-40"
                >
                  Skip for now
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={saving}
                className="flex items-center gap-1.5 bg-indigo-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : step === 2 ? 'Finish' : 'Continue'}
                {!saving && <ChevronRight size={16} />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
